import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { SKILLS, skillMap, STATUS, formatDate, formatRange, groupRequests, dayLabel, romeNow } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Radio, LogOut, Inbox, ShieldCheck, User as UserIcon, CalendarDays, CalendarX, CalendarCheck, CheckCircle2,
  XCircle, Clock, Loader2, Save, MapPin, HelpCircle, Bell, BellOff, ChevronLeft, ChevronRight, Download, MessageSquare,
  Paperclip, StickyNote, FileText, ChevronDown, KeyRound, Settings, ClipboardCheck,
} from "lucide-react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { TechUnavPanel, UnavDialog, RemoveDialog } from "@/components/TechUnav";
import { pushSupported, getPushStatus, enablePush, disablePush } from "@/lib/push";
import { TechnicianChatHub, useUnreadMessages, EventNoteButton } from "@/components/Chat";
import { CalRespond } from "@/components/CalRespond";
import { EventReportButton } from "@/components/EventReport";
import { TechReports, pendingReportGroups } from "@/components/TechReports";
import { MonthlyStatement } from "@/components/MonthlyStatement";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const statusIcon = { in_attesa: Clock, disponibile: CheckCircle2, forse: HelpCircle, non_disponibile: XCircle, confermato: ShieldCheck };
const chipCls = {
  disponibile: "border-slate-700 bg-slate-800 text-emerald-400",
  confermato: "border-blue-600 bg-blue-600 text-white",
  in_attesa: "border-slate-700 bg-slate-800 text-yellow-300",
  forse: "border-slate-700 bg-slate-800 text-violet-300",
  non_disponibile: "border-slate-700 bg-slate-800 text-red-500",
  unav_pending: "border-orange-500/50 border-dashed bg-slate-800 text-orange-300",
};
const dotColor = { in_attesa: "bg-amber-400", disponibile: "bg-lime-400", forse: "bg-violet-400", non_disponibile: "bg-rose-400", confermato: "bg-blue-600" };

function StatusBadge({ status }) {
  const s = STATUS[status] || { label: "In approvazione", color: "bg-orange-500/15 text-orange-300 border-orange-500/30" };
  const Icon = statusIcon[status] || CalendarDays;
  return (
    <span data-testid={`tech-status-${status}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.color}`}>
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
}

function SkillTag({ id }) {
  const s = skillMap[id];
  if (!s) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${s.color}`}>
      <s.icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function DayRow({ r, busy, respond, onMaybe, onYes }) {
  return (
    <div data-testid={`day-row-${r.id}`} className="flex items-center justify-between gap-2 flex-wrap rounded-lg bg-slate-800/50 px-3 py-2">
      <span className="text-sm font-medium text-slate-200 capitalize">{dayLabel(r.date, true)}</span>
      {r.status === "in_attesa" ? (
        <div className="flex gap-1.5">
          <Button size="sm" data-testid={`respond-available-btn-${r.id}`} disabled={busy === r.id} onClick={() => (onYes ? onYes(r.id) : respond(r.id, "disponibile"))}
            className="h-8 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">
            {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Sì</>}
          </Button>
          <Button size="sm" variant="outline" data-testid={`respond-maybe-btn-${r.id}`} disabled={busy === r.id} onClick={() => onMaybe(r.id)}
            className="h-8 border-violet-500/40 text-violet-300 hover:bg-violet-500/10"><HelpCircle className="w-4 h-4 mr-1" /> Forse</Button>
          <Button size="sm" variant="outline" data-testid={`respond-unavailable-btn-${r.id}`} disabled={busy === r.id} onClick={() => respond(r.id, "non_disponibile")}
            className="h-8 border-rose-500/40 text-rose-300 hover:bg-rose-500/10"><XCircle className="w-4 h-4 mr-1" /> No</Button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatusBadge status={r.status} />
          {(r.status === "forse" || r.status === "non_disponibile") && (
            <Button size="sm" data-testid={`change-to-yes-btn-${r.id}`} disabled={busy === r.id} onClick={() => (onYes ? onYes(r.id) : respond(r.id, "disponibile"))}
              className="h-7 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">Sì</Button>
          )}
          {r.status === "forse" && (
            <Button size="sm" variant="outline" data-testid={`change-to-no-btn-${r.id}`} disabled={busy === r.id} onClick={() => respond(r.id, "non_disponibile")}
              className="h-7 border-rose-500/40 text-rose-300 hover:bg-rose-500/10">No</Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function TechnicianApp() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get("tab"); return t === "chat" || t === "calendar" ? t : sessionStorage.getItem("tech-tab") || "requests"; });
  useEffect(() => { sessionStorage.setItem("tech-tab", tab); }, [tab]);
  const unreadMsgs = useUnreadMessages();
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(null);
  const [maybeFor, setMaybeFor] = useState(null);
  const [maybeText, setMaybeText] = useState("");
  const [maybeAns, setMaybeAns] = useState("forse");
  const [logiOpen, setLogiOpen] = useState(null);
  const [logiData, setLogiData] = useState({});
  const [logiBusy, setLogiBusy] = useState(null);
  const [reports, setReports] = useState({ ended: [], reports: [] });
  const [pwdOpen, setPwdOpen] = useState(false);

  const load = async (silent = false) => {
    try {
      const res = await api.get("/requests");
      setRequests(res.data);
      api.get("/reports/mine").then((r) => setReports(r.data)).catch(() => {});
    } catch (err) {
      if (!silent) toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 12000);
    const onVis = () => { if (document.visibilityState === "visible") load(true); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
    // eslint-disable-next-line
  }, []);

  const byEvent = (list) => groupRequests(list, (r) => r.event_id || r.id);
  const openGroups = byEvent(requests.filter((r) => r.status !== "confermato"));
  const pendingGroups = openGroups.filter((g) => g.some((r) => r.status === "in_attesa"));
  const answeredGroups = openGroups.filter((g) => !g.some((r) => r.status === "in_attesa"));
  const pending = pendingGroups;
  const confirmedGroups = byEvent(requests.filter((r) => r.status === "confermato"));
  const reportsDue = pendingReportGroups(confirmedGroups, reports).length;

  const respondAll = async (g) => {
    const ids = g.filter((r) => r.status === "in_attesa").map((r) => r.id);
    setBusy("all");
    try {
      for (const id of ids) await api.put(`/requests/${id}/respond`, { answer: "disponibile" });
      toast.success("Disponibilità registrata per tutti i giorni");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const ackReminder = async (id) => {
    try {
      await api.put(`/requests/${id}/ack-reminder`);
      toast.success("Grazie, promemoria confermato");
      load();
    } catch (err) {
      if (err.response?.status === 404) { toast.error("Il servizio non esiste più: l'evento è stato cancellato"); load(); }
      else toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const respond = async (id, answer, message) => {
    setBusy(id);
    const labels = { disponibile: "Disponibilità registrata", non_disponibile: "Indisponibilità registrata", forse: "Risposta 'Forse' registrata" };
    try {
      await api.put(`/requests/${id}/respond`, { answer, message });
      toast.success(labels[answer] || "Risposta registrata");
      load();
    } catch (err) {
      if (err.response?.status === 404) { toast.error("Questa richiesta non esiste più: l'evento è stato cancellato"); load(); }
      else toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const toggleLogistics = async (id) => {
    if (logiOpen === id) { setLogiOpen(null); return; }
    setLogiOpen(id);
    setLogiBusy(id);
    try {
      const res = await api.get(`/requests/${id}/logistics`);
      setLogiData((p) => ({ ...p, [id]: res.data }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLogiBusy(null);
    }
  };

  const attachmentUrl = (a) => `${API}/attachments/${a.id}/download?auth=${localStorage.getItem("sc_token")}`;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-24">
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/90 backdrop-blur-md"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 1rem)" }}>
        <div className="px-4 h-14 flex items-center justify-between max-w-xl mx-auto">
          <div className="flex items-center gap-2">
            <img src="/logo.webp" alt="GCAVCOM" className="h-7 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="profile-name-btn" onClick={() => setTab("profile")} title="Apri il tuo profilo"
              className={`inline-flex items-center gap-1.5 max-w-[150px] rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${tab === "profile" ? "bg-cyan-500/30 border-cyan-400 text-cyan-100" : "bg-cyan-500/15 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25"}`}>
              <UserIcon className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{user.name}</span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="tech-manage-menu-btn" title="Gestione" className="relative text-slate-400 hover:text-white h-8 w-8">
                  <Settings className="w-4 h-4" />
                  {reportsDue > 0 && <span data-testid="tech-reports-due-badge" className="absolute -top-0.5 -right-0.5 bg-amber-500 text-slate-950 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{reportsDue}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800 text-slate-100">
                <DropdownMenuItem data-testid="tech-menu-reports" onClick={() => setTab("reports")} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <ClipboardCheck className="w-4 h-4 mr-2 text-amber-400" /> Report
                  {reportsDue > 0 && <span className="ml-auto pl-3 text-[10px] font-bold rounded-full bg-amber-500/20 text-amber-300 px-1.5 py-0.5">{reportsDue}</span>}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="tech-menu-statement" onClick={() => setTab("statement")} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <FileText className="w-4 h-4 mr-2 text-emerald-400" /> Resoconto mensile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="tech-change-password-btn" onClick={() => setPwdOpen(true)} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <KeyRound className="w-4 h-4 mr-2 text-cyan-400" /> Cambia password
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn"
              className="text-slate-400 hover:text-white h-8 w-8">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className={`${tab === "statement" ? "max-w-6xl" : "max-w-xl"} mx-auto px-4 py-5`}>
        {tab === "requests" && (
          <div className="space-y-4 animate-fade-up">
            <h1 className="font-display text-2xl font-bold">Richieste in arrivo</h1>
            {pending.length === 0 ? (
              <Empty icon={Inbox} text="Nessuna richiesta in sospeso. Ti avviseremo via email quando ne arriva una." />
            ) : (
              pendingGroups.map((g) => { const r = g[0]; const nPend = g.filter((x) => x.status === "in_attesa").length; return (
                <div key={r.id} data-testid={`pending-request-${r.id}`}
                  className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                  <div className="flex items-center justify-between">
                    <SkillTag id={r.skill} />
                    <span className="text-xs text-amber-300">{nPend} giorn{nPend > 1 ? "i" : "o"} da rispondere</span>
                  </div>
                  <h2 className="font-display text-lg font-semibold mt-3">{r.event_name || r.title || "Disponibilità"}{r.supervisor && <span className="ml-2 align-middle text-[10px] font-bold rounded px-1.5 py-0.5 border border-violet-500/40 bg-violet-500/10 text-violet-300">SUPERVISORE</span>}</h2>
                  <p className="text-sm text-slate-400">Cliente: {r.client_name}</p>
                  <div className="flex items-center gap-3 text-sm text-slate-400 mt-2 flex-wrap">
                    <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4 text-cyan-400" /> {formatRange(g[0].date, g[g.length - 1].date)}</span>
                    {r.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {r.location}</span>}
                  </div>
                  {r.note && <p className="text-sm text-slate-300 mt-2 bg-slate-800/60 rounded-lg p-2.5">{r.note}</p>}
                  <div className="mt-4 space-y-2">
                    {g.map((d) => (
                      <div key={d.id}>
                        <DayRow r={d} busy={busy} respond={respond} onMaybe={(id) => { setMaybeFor(id); setMaybeAns("forse"); setMaybeText(""); }}
                          onYes={(id) => { setMaybeFor(id); setMaybeAns("disponibile"); setMaybeText(""); }} />
                        {maybeFor === d.id && (
                          <div className="mt-2 space-y-2" data-testid={`maybe-box-${d.id}`}>
                            <Textarea data-testid={`maybe-text-${d.id}`} value={maybeText} onChange={(e) => setMaybeText(e.target.value)}
                              placeholder={maybeAns === "forse" ? "Spiega il motivo del 'Forse' (opzionale)" : "Aggiungi una nota per il manager (opzionale)"} className="bg-slate-800 border-slate-700" />
                            <div className="flex gap-2">
                              <Button data-testid={`maybe-send-${d.id}`} disabled={busy === d.id}
                                onClick={() => { respond(d.id, maybeAns, maybeText); setMaybeFor(null); }}
                                className={maybeAns === "forse" ? "bg-violet-500 hover:bg-violet-600 text-white font-semibold" : "bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold"}>
                                {maybeAns === "forse" ? "Invia “Forse”" : "Invia “Sì”"}</Button>
                              <Button variant="outline" onClick={() => setMaybeFor(null)} className="border-slate-700 text-slate-300">Annulla</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {nPend > 1 && (
                    <Button data-testid={`respond-all-yes-${r.id}`} disabled={busy === "all"} onClick={() => respondAll(g)}
                      className="w-full mt-3 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 font-semibold">
                      {busy === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Sì a tutti i giorni rimasti</>}
                    </Button>
                  )}
                </div>
              ); })
            )}

            {answeredGroups.length > 0 && (
              <div className="pt-2">
                <h3 className="text-sm uppercase tracking-wider font-mono text-slate-500 mb-3">Risposte inviate</h3>
                <div className="space-y-2">
                  {answeredGroups.map((g) => (
                    <div key={g[0].id} data-testid={`answered-request-${g[0].id}`} className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
                      <p className="font-medium truncate">{g[0].event_name || g[0].title || "Disponibilità"}</p>
                      <p className="text-xs text-slate-500 mb-2">{formatRange(g[0].date, g[g.length - 1].date)} · il “Sì” non è modificabile</p>
                      <div className="space-y-1.5">
                        {g.map((d) => <DayRow key={d.id} r={d} busy={busy} respond={respond} onMaybe={() => {}} />)}
                      </div>
                      {g[0].event_id && g.some((d) => d.status === "disponibile") && <EventNoteButton eventId={g[0].event_id} eventName={g[0].event_name || "Evento"} />}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "confirmed" && (
          <div className="space-y-4 animate-fade-up">
            <h1 className="font-display text-2xl font-bold">Servizi confermati</h1>
            {confirmedGroups.length === 0 ? (
              <Empty icon={ShieldCheck} text="Nessun servizio confermato dal manager al momento." />
            ) : (
              confirmedGroups.map((g) => { const r = { ...g[0], date_end: g[g.length - 1].date }; return (
                <div key={r.id} data-testid={`confirmed-${r.id}`}
                  className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-5">
                  <div className="flex items-center justify-between">
                    <SkillTag id={r.skill} />
                    <StatusBadge status={r.status} />
                  </div>
                  <h2 className="font-display text-lg font-semibold mt-3">{r.event_name || r.title || "Disponibilità"}{r.supervisor && <span className="ml-2 align-middle text-[10px] font-bold rounded px-1.5 py-0.5 border border-violet-500/40 bg-violet-500/10 text-violet-300">SUPERVISORE</span>}</h2>
                  {g.filter((d) => d.service_reminder && !d.service_reminder_ack_at).map((d) => (
                    <div key={d.id} data-testid={`reminder-banner-${d.id}`} className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                      <span className="flex items-center gap-2 text-sm text-amber-200"><Bell className="w-4 h-4" /> Promemoria: servizio {d.service_reminder.split(":")[1]} ({dayLabel(d.date)})</span>
                      <Button size="sm" data-testid={`reminder-ack-btn-${d.id}`} onClick={() => ackReminder(d.id)}
                        className="h-8 bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold"><CheckCircle2 className="w-4 h-4 mr-1" /> Ho letto</Button>
                    </div>
                  ))}
                  {r.client_name && <p className="text-sm text-slate-400">Cliente: {r.client_name}</p>}
                  <div className="flex items-center gap-3 text-sm text-slate-300 mt-2 flex-wrap">
                    <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4 text-cyan-400" /> {g.length > 1 ? g.map((d) => dayLabel(d.date)).join(", ") : formatRange(r.date, r.date_end)}</span>
                    {r.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {r.location}</span>}
                  </div>
                  <a href={googleCalUrl(r)} target="_blank" rel="noopener noreferrer" data-testid={`gcal-btn-${r.id}`}
                    className="inline-flex items-center gap-1.5 mt-3 rounded-lg border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 px-3 py-1.5 text-sm font-medium">
                    <CalendarDays className="w-4 h-4" /> Aggiungi a Google Calendar
                  </a>
                  {r.archived && <p data-testid={`archived-tag-${r.id}`} className="mt-2 text-xs text-slate-400">Evento chiuso e archiviato dal manager: resta nel tuo storico e nel resoconto mensile.</p>}
                  {r.event_id && !r.archived && <div><EventNoteButton eventId={r.event_id} eventName={r.event_name || "Evento"} /></div>}
                  {r.event_id && !r.archived && reports.ended.includes(r.event_id) && (
                    <EventReportButton group={g} report={reports.reports.find((x) => x.event_id === r.event_id)} onDone={() => load(true)} />
                  )}
                  {!r.archived && <button data-testid={`logistics-toggle-${r.id}`} onClick={() => toggleLogistics(r.id)}
                    className="w-full mt-3 flex items-center justify-between rounded-lg border border-slate-700 bg-slate-900/60 hover:bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-200 transition-colors">
                    <span className="flex items-center gap-2"><Paperclip className="w-4 h-4 text-cyan-400" /> Note logistica e allegati</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${logiOpen === r.id ? "rotate-180" : ""}`} />
                  </button>}
                  {logiOpen === r.id && !r.archived && (
                    <div data-testid={`logistics-panel-${r.id}`} className="mt-3 space-y-3">
                      {logiBusy === r.id ? (
                        <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
                      ) : (
                        <>
                          <div>
                            <p className="text-xs uppercase tracking-wider font-mono text-slate-500 mb-1.5 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Note logistica</p>
                            {logiData[r.id]?.notes ? (
                              <p data-testid={`logistics-notes-${r.id}`} className="text-sm text-slate-200 whitespace-pre-wrap bg-slate-800/60 rounded-lg p-3">{logiData[r.id].notes}</p>
                            ) : (
                              <p className="text-sm text-slate-500">Nessuna nota disponibile.</p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wider font-mono text-slate-500 mb-1.5 flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Allegati</p>
                            {logiData[r.id]?.attachments?.length > 0 ? (
                              <div className="space-y-2">
                                {logiData[r.id].attachments.map((a) => (
                                  <a key={a.id} href={attachmentUrl(a)} target="_blank" rel="noopener noreferrer"
                                    data-testid={`attachment-${a.id}`}
                                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-700 bg-slate-900/60 hover:bg-slate-800/60 px-3 py-2 text-sm transition-colors">
                                    <span className="flex items-center gap-2 min-w-0 text-slate-200"><FileText className="w-4 h-4 text-cyan-400 shrink-0" /> <span className="truncate">{a.original_filename}</span></span>
                                    <Download className="w-4 h-4 text-slate-400 shrink-0" />
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500">Nessun allegato disponibile.</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ); })
            )}
          </div>
        )}

        {tab === "calendar" && <TechCalendar requests={requests} onChanged={() => load(true)} />}

        {tab === "profile" && <Profile />}

        {tab === "statement" && <MonthlyStatement requests={requests} reports={reports} />}

        {tab === "reports" && <TechReports groups={confirmedGroups} reports={reports} onDone={() => load(true)} />}

        {tab === "chat" && <TechnicianChatHub />}
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 border-t border-slate-800 bg-slate-950/95 backdrop-blur-md">
        <div className="max-w-xl mx-auto grid grid-cols-4">
          {[
            { id: "requests", label: "Richieste", Icon: Inbox, badge: pending.length },
            { id: "calendar", label: "Calendario", Icon: CalendarDays, badge: 0 },
            { id: "confirmed", label: "Confermati", Icon: ShieldCheck, badge: 0 },
            { id: "chat", label: "Chat", Icon: MessageSquare, badge: tab === "chat" ? 0 : unreadMsgs },
          ].map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => setTab(n.id)}
              className={`relative flex flex-col items-center gap-1 py-3 transition-colors ${tab === n.id ? "text-cyan-400" : "text-slate-500"}`}>
              <n.Icon className="w-5 h-5" />
              <span className="text-[11px] font-medium">{n.label}</span>
              {n.badge > 0 && (
                <span className="absolute top-1.5 right-1/2 translate-x-4 bg-amber-500 text-slate-950 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {n.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

function buildIcs(reqs) {
  const pad = (n) => String(n).padStart(2, "0");
  const toDate = (s) => s.replace(/-/g, "");
  const addDay = (s) => {
    const d = new Date(s + "T00:00:00");
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  };
  const now = new Date();
  const stamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
  const esc = (t) => (t || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//GCAVCOM Crew//IT", "CALSCALE:GREGORIAN"];
  reqs.forEach((r) => {
    lines.push("BEGIN:VEVENT");
    lines.push(`UID:${r.id}@gcavcom-crew`);
    lines.push(`DTSTAMP:${stamp}`);
    lines.push(`DTSTART;VALUE=DATE:${toDate(r.date)}`);
    lines.push(`DTEND;VALUE=DATE:${addDay(r.date_end || r.date)}`);
    lines.push(`SUMMARY:${esc(r.event_name || r.title || "Servizio GCAVCOM")}`);
    if (r.location) lines.push(`LOCATION:${esc(r.location)}`);
    const desc = [r.client_name ? `Cliente: ${r.client_name}` : null, r.skill ? `Ruolo: ${r.skill}` : null].filter(Boolean).join(" - ");
    if (desc) lines.push(`DESCRIPTION:${esc(desc)}`);
    lines.push("STATUS:CONFIRMED");
    lines.push("END:VEVENT");
  });
  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

function googleCalUrl(r) {
  const pad = (n) => String(n).padStart(2, "0");
  const toDate = (s) => s.replace(/-/g, "");
  const addDay = (s) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() + 1); return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`; };
  const start = toDate(r.date);
  const end = addDay(r.date_end || r.date);
  const text = encodeURIComponent(r.event_name || r.title || "Servizio GCAVCOM");
  const details = encodeURIComponent([r.client_name ? `Cliente: ${r.client_name}` : null, r.skill ? `Ruolo: ${r.skill}` : null].filter(Boolean).join(" - "));
  const loc = encodeURIComponent(r.location || "");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${start}/${end}&details=${details}&location=${loc}`;
}

function TechCalendar({ requests, onChanged }) {
  const today = romeNow();
  const [cursor, setCursor] = useState(() => { const s = sessionStorage.getItem("tech-cal-month"); return s ? new Date(s) : new Date(today.getFullYear(), today.getMonth(), 1); });
  useEffect(() => { sessionStorage.setItem("tech-cal-month", cursor.toISOString()); }, [cursor]);
  const [selected, setSelected] = useState(null);
  const [onlyConfirmed, setOnlyConfirmed] = useState(() => sessionStorage.getItem("tech-cal-only") === "1");
  useEffect(() => { sessionStorage.setItem("tech-cal-only", onlyConfirmed ? "1" : "0"); }, [onlyConfirmed]);
  const [unav, setUnav] = useState([]);
  const [unavKey, setUnavKey] = useState(0);
  useEffect(() => {
    Promise.all([api.get("/unavailability/me"), api.get("/unavailability/me/requests")]).then(([a, b]) => setUnav([
      ...a.data.map((u) => ({ id: `unav-${u.id}`, status: "non_disponibile", date: u.start, date_end: u.end, title: "Non disponibile" })),
      ...b.data.map((u) => ({ id: `unavreq-${u.id}`, status: "unav_pending", date: u.start, date_end: u.end, title: "In appr." })),
    ])).catch(() => {});
  }, [requests, unavKey]);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const pad = (n) => String(n).padStart(2, "0");
  const dstr = (d) => `${year}-${pad(month + 1)}-${pad(d)}`;
  const startDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const items = onlyConfirmed ? requests.filter((r) => r.status === "confermato") : [...requests, ...unav];
  const reqsForDay = (d) => {
    const ds = dstr(d);
    return items.filter((r) => ds >= r.date && ds <= (r.date_end || r.date));
  };
  const exportIcs = () => {
    const confirmed = requests.filter((r) => r.status === "confermato");
    if (confirmed.length === 0) { toast.error("Nessun servizio confermato da esportare"); return; }
    const blob = new Blob([buildIcs(confirmed)], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "gcavcom-servizi-confermati.ics";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${confirmed.length} servizi confermati esportati`);
  };
  const exportPdf = async () => {
    try {
      const res = await api.get("/export/my-agenda", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "agenda-servizi.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Agenda PDF scaricata");
    } catch (err) { toast.error("Export PDF non riuscito"); }
  };
  const order = { confermato: 0, disponibile: 1, forse: 2, in_attesa: 3, non_disponibile: 4 };
  const monthLabel = cursor.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const selReqs = selected ? reqsForDay(selected) : [];
  const monthPending = requests.filter((r) => r.status === "in_attesa" && r.date <= dstr(daysInMonth) && (r.date_end || r.date) >= dstr(1)).length;
  const [unavDay, setUnavDay] = useState(null);
  const [freeDay, setFreeDay] = useState(null);
  const canFree = selected && dstr(selected) >= todayStr && selReqs.some((r) => r.status === "non_disponibile" && String(r.id).startsWith("unav-"));
  const canUnav = selected && dstr(selected) >= todayStr && !selReqs.some((r) => ["in_attesa", "disponibile", "confermato", "unav_pending"].includes(r.status) || String(r.id).startsWith("unav-"));
  const lastTap = useRef({ d: null, t: 0 });
  const quickUnav = (d) => {
    const ds = dstr(d);
    const rs = reqsForDay(d);
    if (ds < todayStr) { toast.error("Non puoi modificare giorni passati"); return; }
    if (rs.some((r) => String(r.id).startsWith("unav-"))) { setFreeDay({ start: ds, end: ds }); return; }
    if (rs.some((r) => r.status === "unav_pending")) { toast.info("Hai già segnalato questo giorno: è in attesa di approvazione"); return; }
    if (rs.some((r) => ["in_attesa", "disponibile", "confermato"].includes(r.status))) {
      toast.error("Non puoi segnarti non disponibile: in questo giorno hai una richiesta senza risposta, hai dato disponibilità o sei confermato. Scrivi al manager in chat.", { duration: 7000 });
      return;
    }
    setUnavDay(ds);
  };
  const onDayTap = (d) => {
    const now = Date.now();
    if (lastTap.current.d === d && now - lastTap.current.t < 450) { lastTap.current = { d: null, t: 0 }; setSelected(d); quickUnav(d); return; }
    lastTap.current = { d, t: now };
    setSelected((s) => (s === d ? null : d));
  };

  return (
    <div className="space-y-4 animate-fade-up" data-testid="tech-calendar">
      <h1 className="font-display text-2xl font-bold">Il mio calendario</h1>
      <p className="text-xs text-slate-400 -mt-2" data-testid="tech-cal-doubletap-hint">Tocca due volte un giorno per segnarti non disponibile (o per tornare disponibile). Tocca un giorno con una richiesta gialla per rispondere Sì, No o Forse.</p>
      <TechUnavPanel reloadKey={unavKey} onChanged={() => setUnavKey((k) => k + 1)} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 rounded-lg border border-slate-800 bg-slate-900 p-1">
          <button data-testid="cal-filter-all" onClick={() => setOnlyConfirmed(false)}
            className={`text-xs font-medium rounded-md px-3 py-1.5 transition-colors ${!onlyConfirmed ? "bg-cyan-500 text-slate-950" : "text-slate-400 hover:text-slate-200"}`}>Disponibilità</button>
          <button data-testid="cal-filter-confirmed" onClick={() => setOnlyConfirmed(true)}
            className={`text-xs font-medium rounded-md px-3 py-1.5 transition-colors ${onlyConfirmed ? "bg-blue-500 text-white" : "text-slate-400 hover:text-slate-200"}`}>SOLO conferme</button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Esporta agenda:</span>
          <Button data-testid="export-ics-btn" onClick={exportIcs} variant="outline" size="sm"
            className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10">
            <Download className="w-4 h-4 mr-1.5" /> ICS
          </Button>
          <Button data-testid="export-pdf-btn" onClick={exportPdf} variant="outline" size="sm"
            className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10">
            <FileText className="w-4 h-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>
      {!onlyConfirmed && (
        <div data-testid="tech-cal-month-pending"
          className={`rounded-xl border px-3 py-2 text-sm font-medium ${monthPending ? "border-yellow-400/60 bg-yellow-400/10 text-yellow-200" : "border-slate-800 bg-slate-900 text-slate-400"}`}>
          {monthPending
            ? <>Hai <strong data-testid="tech-cal-month-pending-count">{monthPending}</strong> richiest{monthPending > 1 ? "e" : "a"} senza risposta in {monthLabel}</>
            : <>Nessuna richiesta senza risposta in {monthLabel}</>}
        </div>
      )}
      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-3">
          <button data-testid="tech-cal-prev" onClick={() => { setCursor(new Date(year, month - 1, 1)); setSelected(null); }}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-300"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-display font-semibold capitalize">{monthLabel}</span>
          <button data-testid="tech-cal-next" onClick={() => { setCursor(new Date(year, month + 1, 1)); setSelected(null); }}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-300"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-slate-500 mb-1">
          {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} />;
            const ds = dstr(d);
            const reqs = reqsForDay(d);
            const chips = [...reqs].sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));
            const isToday = ds === todayStr;
            const isSel = selected === d;
            return (
              <button key={d} data-testid={`tech-cal-day-${ds}`} onClick={() => onDayTap(d)} style={{ touchAction: "manipulation" }}
                className={`min-h-[64px] sm:min-h-[84px] rounded-lg border p-1 flex flex-col items-stretch text-[11px] transition-colors overflow-hidden ${isSel ? "border-cyan-400 bg-cyan-500/10" : isToday ? "border-cyan-500/40 bg-slate-800/60" : "border-slate-800 bg-slate-900/60"}`}>
                <span className={`text-center ${isToday ? "text-cyan-300 font-semibold" : "text-slate-300"}`}>{d}</span>
                <div className="mt-0.5 space-y-0.5">
                  {chips.slice(0, 3).map((r) => {
                    return (
                      <span key={r.id} data-testid={`tech-cal-chip-${r.id}`}
                        className={`relative block rounded border px-0.5 py-px text-[8.5px] sm:text-[10px] font-semibold leading-[11px] sm:leading-[13px] text-left ${chipCls[r.status] || "border-slate-700 bg-slate-800 text-slate-200"} ${r.status === "in_attesa" && !onlyConfirmed ? "!border-yellow-400 !bg-yellow-400/15" : ""}`}>
                        <span className={`block ${r.status === "in_attesa" && !onlyConfirmed ? "whitespace-nowrap overflow-hidden text-[7px] sm:text-[10px] tracking-[-0.03em] -mx-0.5 text-center" : "line-clamp-3 [overflow-wrap:anywhere]"}`}>{r.status === "in_attesa" && !onlyConfirmed ? "Richiesta" : (r.event_name || r.title || "Disponibilità")}</span>
                      </span>
                    );
                  })}
                  {chips.length > 3 && <span className="block text-center text-[9px] text-slate-400">+{chips.length - 3}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-slate-400">
        {[["disponibile", "Disponibile"], ["forse", "Forse"], ["non_disponibile", "Non disponibile"], ["in_attesa", "In attesa"], ["confermato", "Confermato"], ["unav_pending", "Indisp. in approvazione"]].map(([k, l]) => (
          <span key={k} data-testid={`tech-cal-legend-${k}`} className={`rounded border px-1.5 py-0.5 font-semibold ${chipCls[k]}`}>{l}</span>
        ))}
      </div>

      {selected && (
        <div className="space-y-2" data-testid="tech-cal-day-detail">
          <h3 className="text-sm uppercase tracking-wider font-mono text-slate-500">{dayLabel(dstr(selected), true)}</h3>
          {canUnav && (
            <Button data-testid="tech-cal-day-unav-btn" variant="outline" onClick={() => setUnavDay(dstr(selected))}
              className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10"><CalendarX className="w-4 h-4 mr-2" />Non sono disponibile in questa data</Button>
          )}
          {canFree && (
            <Button data-testid="tech-cal-day-free-btn" variant="outline" onClick={() => setFreeDay({ start: dstr(selected), end: dstr(selected) })}
              className="w-full border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"><CalendarCheck className="w-4 h-4 mr-2" />Torno disponibile in questa data</Button>
          )}
          {selReqs.length === 0 ? (
            <p className="text-sm text-slate-500">Nessun impegno in questa data.</p>
          ) : selReqs.map((r) => r.status === "confermato" ? <AgendaItem key={r.id} r={r} /> : (r.status === "in_attesa" && !onlyConfirmed && !String(r.id).startsWith("unav")) ? (
            <CalRespond key={r.id} r={r} dateLabel={dayLabel(dstr(selected), true)} onDone={onChanged} />
          ) : (
            <div key={r.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-800 bg-slate-900/60 p-3">
              <div className="min-w-0">
                <p className="font-medium truncate">{r.event_name || r.title || "Disponibilità"}</p>
                <p className="text-xs text-slate-500">{formatRange(r.date, r.date_end)}</p>
              </div>
              <StatusBadge status={r.status} />
            </div>
          ))}
        </div>
      )}
      <RemoveDialog unav={freeDay} onClose={() => setFreeDay(null)} onDone={() => setUnavKey((k) => k + 1)} />
      <UnavDialog open={!!unavDay} initialDate={unavDay || ""} onOpenChange={(o) => { if (!o) setUnavDay(null); }} onSent={() => setUnavKey((k) => k + 1)} />
    </div>
  );
}

function AgendaItem({ r }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get(`/requests/${r.id}/logistics`).then((res) => setInfo(res.data)).catch(() => setInfo({}));
  }, [r.id]);
  const url = (a) => `${API}/attachments/${a.id}/download?auth=${localStorage.getItem("sc_token")}`;
  return (
    <div data-testid={`agenda-item-${r.id}`} className="rounded-xl border border-blue-500/40 bg-slate-900/80 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold truncate">{r.event_name || (r.event_id ? "Servizio" : r.status === "disponibile" ? "Disponibile" : "Disponibilità")}</p>
        <StatusBadge status={r.status} />
      </div>
      {!info ? (
        <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      ) : (
        <div className="space-y-1.5 text-sm text-slate-300">
          <p data-testid={`agenda-time-${r.id}`} className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-cyan-400" />
            {r.slot_start ? `${r.slot_start} – ${r.slot_end}` : info.start_time ? `${info.start_time}${info.end_time ? ` – ${info.end_time}` : ""}` : "Orario da definire"}</p>
          {info.location && (
            <a data-testid={`agenda-location-${r.id}`} href={`https://maps.google.com/?q=${encodeURIComponent(info.location)}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 hover:text-cyan-300"><MapPin className="w-4 h-4 text-cyan-400" /> {info.location}</a>
          )}
          {info.client_name && <p className="text-xs text-slate-500">Cliente: {info.client_name}</p>}
          <div>
            <p className="text-xs uppercase tracking-wider font-mono text-slate-500 mb-1 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Note logistica</p>
            <p data-testid={`agenda-notes-${r.id}`} className="whitespace-pre-wrap bg-slate-800/60 rounded-lg p-2.5 text-slate-200">{info.notes || "Nessuna nota disponibile."}</p>
          </div>
          {info.attachments?.length > 0 && info.attachments.map((a) => (
            <a key={a.id} href={url(a)} target="_blank" rel="noopener noreferrer" data-testid={`agenda-attachment-${a.id}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-slate-700 bg-slate-900/60 hover:bg-slate-800/60 px-3 py-2">
              <span className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-cyan-400 shrink-0" /> <span className="truncate">{a.original_filename}</span></span>
              <Download className="w-4 h-4 text-slate-400 shrink-0" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function Empty({ icon: Icon, text }) {  return (
    <div className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/40 p-10 text-center">
      <Icon className="w-10 h-10 text-slate-700 mx-auto mb-3" />
      <p className="text-slate-500 text-sm">{text}</p>
    </div>
  );
}

function NotificationsCard() {
  const [status, setStatus] = useState("default");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPushStatus().then(setStatus).catch(() => setStatus("unsupported"));
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setStatus("subscribed");
      toast.success("Notifiche attivate su questo dispositivo");
    } catch (err) {
      toast.error(err.message || "Impossibile attivare le notifiche");
      setStatus(await getPushStatus());
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disablePush();
      setStatus("default");
      toast.success("Notifiche disattivate su questo dispositivo");
    } finally {
      setBusy(false);
    }
  };

  const isSub = status === "subscribed";
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 space-y-3" data-testid="notifications-card">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isSub ? "bg-cyan-500/15 text-cyan-300" : "bg-slate-800 text-slate-400"}`}>
          {isSub ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
        </div>
        <div className="min-w-0">
          <p className="font-semibold">Notifiche push</p>
          <p className="text-sm text-slate-500">Ricevi un avviso sul telefono per nuove richieste, solleciti e conferme.</p>
        </div>
      </div>

      {status === "unsupported" && (
        <p className="text-sm text-amber-400" data-testid="push-unsupported">
          Il dispositivo non supporta le notifiche push. Su iPhone, installa prima l'app sulla Home (Safari → Condividi → "Aggiungi a schermata Home").
        </p>
      )}
      {status === "denied" && (
        <p className="text-sm text-rose-400" data-testid="push-denied">
          Permesso notifiche bloccato. Abilitalo dalle impostazioni del browser per questo sito.
        </p>
      )}

      {status !== "unsupported" && status !== "denied" && (
        isSub ? (
          <Button onClick={disable} disabled={busy} variant="outline" data-testid="disable-push-btn"
            className="w-full border-slate-700 text-slate-300 hover:bg-slate-800">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellOff className="w-4 h-4 mr-2" />}
            Disattiva notifiche
          </Button>
        ) : (
          <Button onClick={enable} disabled={busy} data-testid="enable-push-btn"
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
            Attiva notifiche
          </Button>
        )
      )}
    </div>
  );
}

function Profile() {
  const { user, setUser } = useAuth();
  const [phone, setPhone] = useState(user.phone || "");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      const res = await api.put("/auth/profile", { phone });
      setUser(res.data);
      toast.success("Profilo aggiornato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <h1 className="font-display text-2xl font-bold">Profilo & Skill</h1>
      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-cyan-500/15 text-cyan-300 flex items-center justify-center text-lg font-semibold">
            {user.name?.charAt(0)}
          </div>
          <div>
            <p className="font-semibold">{user.name}</p>
            <p className="text-sm text-slate-500">{user.email}</p>
          </div>
        </div>
      </div>

      <NotificationsCard />

      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 space-y-4">
        <div className="space-y-2">
          <Label>Le tue skill</Label>
          {user.skills?.length ? (
            <div className="flex flex-wrap gap-2">
              {user.skills.map((id) => <SkillTag key={id} id={id} />)}
            </div>
          ) : (
            <p className="text-sm text-slate-500">Nessuna skill assegnata.</p>
          )}
          <p className="text-xs text-slate-500">Le skill vengono impostate dal manager.</p>
        </div>
        <div className="space-y-2">
          <Label>Telefono</Label>
          <Input data-testid="profile-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="+39 ..." className="bg-slate-800 border-slate-700" />
        </div>
        <Button onClick={save} disabled={loading} data-testid="save-profile-btn"
          className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Salva profilo
        </Button>
      </div>
    </div>
  );
}
